import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import Editor from "@/pages/Editor";
import ClipsLibrary from "@/pages/ClipsLibrary";
import Workspace from "@/pages/Workspace";
import { Layout } from "@/components/Layout";
import { LoadingScreen } from "@/components/LoadingScreen";
import { CookieConsent } from "@/components/CookieConsent";
import LegalPage from "@/pages/LegalPage";
import Overlay from "@/pages/Overlay";
import LivePanel from "@/pages/LivePanel";
import GiftPage from "@/pages/GiftPage";
import Integrations from "@/pages/Integrations";
import Releases from "@/pages/Releases";
import Suggestions from "@/pages/Suggestions";
import Support from "@/pages/Support";
import MasterCenter from "@/pages/MasterCenter";
import Landing from "@/pages/Landing";
import SharedClip from "@/pages/SharedClip";
import Stats from "@/pages/Stats";
import Vitrine from "@/pages/Vitrine";
import CreatorProfile from "@/pages/CreatorProfile";
import Challenges from "@/pages/Challenges";
import Following from "@/pages/Following";
import Credits from "@/pages/Credits";
import Guide from "@/pages/Guide";
import Referral from "@/pages/Referral";
import ReferralLanding from "@/pages/ReferralLanding";
import Parrains from "@/pages/Parrains";
import Tarifs from "@/pages/Tarifs";
import { HolidaySkin } from "@/components/holiday/HolidaySkin";
import TrailerUnsubscribe from "@/pages/TrailerUnsubscribe";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import { FollowConfirm, FollowUnsubscribe } from "@/pages/FollowPages";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import { useLocation } from "react-router-dom";
import { NavHelpers, Fade } from "@/components/NavHelpers";
import { AdCapture } from "@/components/AdCapture";

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null)
    return <LoadingScreen testid="auth-loading" />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

const PublicOnly = ({ children }) => {
  const { user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

const ConsentGate = () => (/^\/(overlay|panel)\//.test(useLocation().pathname) ? null : <CookieConsent />);

function App() {
  return (
    <div className="App cx-noise">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <HolidaySkin />
            <Routes>
              <Route path="/" element={<Fade><Landing /></Fade>} />
              <Route path="/c/:id" element={<Fade><SharedClip /></Fade>} />
              <Route path="/vitrine" element={<Fade><Vitrine /></Fade>} />
              <Route path="/tarifs" element={<Fade><Tarifs /></Fade>} />
              <Route path="/defis" element={<Fade><Challenges /></Fade>} />
              <Route path="/u/:handle" element={<Fade><CreatorProfile /></Fade>} />
              <Route path="/follow/confirm" element={<Fade><FollowConfirm /></Fade>} />
              <Route path="/follow/unsubscribe" element={<Fade><FollowUnsubscribe /></Fade>} />
              <Route path="/login" element={<PublicOnly><Fade><AuthPage /></Fade></PublicOnly>} />
              <Route path="/forgot-password" element={<PublicOnly><Fade><ForgotPassword /></Fade></PublicOnly>} />
              <Route path="/reset-password" element={<Fade><ResetPassword /></Fade>} />
              <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
              <Route path="/editor/:id" element={<Protected><Editor /></Protected>} />
              <Route path="/clips" element={<Protected><ClipsLibrary /></Protected>} />
              <Route path="/workspace" element={<Protected><Workspace /></Protected>} />
              <Route path="/integrations" element={<Protected><Integrations /></Protected>} />
              <Route path="/nouveautes" element={<Protected><Releases /></Protected>} />
              <Route path="/stats" element={<Protected><Stats /></Protected>} />
              <Route path="/abonnements" element={<Protected><Following /></Protected>} />
              <Route path="/credits" element={<Protected><Credits /></Protected>} />
              <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
              <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
              <Route path="/suggestions" element={<Protected><Suggestions /></Protected>} />
              <Route path="/guide" element={<Protected><Guide /></Protected>} />
              <Route path="/parrainage" element={<Protected><Referral /></Protected>} />
              <Route path="/r/:code" element={<Fade><ReferralLanding /></Fade>} />
              <Route path="/parrains" element={<Fade><Parrains /></Fade>} />
              <Route path="/trailer/unsubscribe" element={<Fade><TrailerUnsubscribe /></Fade>} />
              <Route path="/support" element={<Protected><Support /></Protected>} />
              <Route path="/admin" element={<Protected><MasterCenter /></Protected>} />
              <Route path="/confidentialite" element={<Fade><LegalPage kind="privacy" /></Fade>} />
              <Route path="/conditions" element={<Fade><LegalPage kind="terms" /></Fade>} />
              <Route path="/overlay/:key" element={<Overlay />} />
              <Route path="/panel/:key" element={<LivePanel />} />
              <Route path="/gift/:id" element={<Fade><GiftPage /></Fade>} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            <NavHelpers />
            <AdCapture />
            <ConsentGate />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
      <Toaster theme="dark" position={window.location.pathname.startsWith("/panel/") ? "bottom-center" : "top-center"} toastOptions={{ style: { background: "#0e1222", border: "1px solid rgba(167,70,255,0.35)", color: "#eef1f8" } }} />
    </div>
  );
}

export default App;
